import { createContext, useContext, type ReactNode } from 'react';
import type { LensMapPin, LensMapRoute } from '../../requested/lens_map.js';
import type { ApprovalsScreenCopy } from '../approvals_copy.js';
import type { ApprovalsPlaces } from '../approvals_screen_model.js';

/*
 * What the Approvals screen receives from the host besides the payload (#5002):
 * the copy, the locale that formats dates and names, the place lookups, the
 * destination lens, the map token and the notice port. It all arrives through
 * the screen's `env` prop and travels inside through a PRIVATE context — the
 * same seam as the flight explorer's `env`: no host provides or reads it.
 */

/** What the lens highlights: one trip, or one airport and the trips through it. */
export type ApprovalsLensSelection =
  | { kind: 'trip'; tripId: string }
  | { kind: 'airport'; code: string }
  | null;

/** A queue trip as the lens reads it: the card key and its services' vouchers. */
export type ApprovalsLensTrip = { id: string; voucherIds: readonly string[] };

/** The lens for one place and selection: what the `LensMap` draws, which trips
 *  stay in focus, and the hotels without a position (named under the map). */
export type ApprovalsLensView = {
  center: { latitude: number; longitude: number } | null;
  pins: LensMapPin[];
  route: LensMapRoute | undefined;
  focusTripIds: ReadonlySet<string> | null;
  hotels: string[];
};

/** The destination lens model; the host owns the airport table and the
 *  voucher details it reads. */
export type ApprovalsPlaceLens = {
  view: (
    trips: readonly ApprovalsLensTrip[],
    city: string,
    selection: ApprovalsLensSelection,
  ) => ApprovalsLensView;
  /** The trip of a hotel pin; `null` for an airport pin. */
  tripOfPin: (trips: readonly ApprovalsLensTrip[], pinId: string) => string | null;
};

/** A decision notice; the host draws it (a toast in the web-chat). */
export type ApprovalsNotice = { title: string; description: string };

export type ApprovalsScreenEnv = {
  readonly copy: ApprovalsScreenCopy;
  readonly locale: string;
  readonly places: ApprovalsPlaces;
  readonly placeLens: ApprovalsPlaceLens;
  /** Without a token the lens map falls back to the named placeholder. */
  readonly mapboxAccessToken?: string | undefined;
  readonly notify: (notice: ApprovalsNotice) => void;
};

const ApprovalsEnvContext = createContext<ApprovalsScreenEnv | null>(null);

export function ApprovalsEnvProvider({
  env,
  children,
}: Readonly<{ env: ApprovalsScreenEnv; children: ReactNode }>) {
  return <ApprovalsEnvContext.Provider value={env}>{children}</ApprovalsEnvContext.Provider>;
}

/** The screen env in scope. Outside the screen it is a mounting error, not a
 *  state: every piece here only exists inside it. */
export function useApprovalsEnv(): ApprovalsScreenEnv {
  const env = useContext(ApprovalsEnvContext);
  if (!env) throw new Error('approvals: piece mounted outside ApprovalsScreen');
  return env;
}
